/* eslint-disable import/prefer-default-export */
/* eslint-disable prefer-promise-reject-errors */
import { memoize } from 'lodash';
import { t } from 'i18next';

/**
 * A function to check wether a string is a subdomain of another string
 * @param string1 the value to check
 * @param string2 the value to check against
 */
const isSubdomain = memoize(
  (string1: string, string2: string) => {
    const string1Parts = string1.split('.');
    const string2Parts = string2.split('.');
    const string1Length = string1Parts.length;
    const string2Length = string2Parts.length;

    if (string1Length < string2Length) {
      return false;
    }

    for (let i = 0; i < string2Length; i += 1) {
      if (string1Parts[string1Length - i - 1] !== string2Parts[string2Length - i - 1]) {
        return false;
      }
    }

    return true;
  },
  (string1, string2) => `${string1}-${string2}`,
);

/**
 * A function to check wether a string is a parent domain of another string
 * @param string1 the value to check
 * @param string2 the value to check against
 */
const isParentDomain = memoize(
  (string1: string, string2: string) => {
    const string1Parts = string1.split('.');
    const string2Parts = string2.split('.');
    const string1Length = string1Parts.length;
    const string2Length = string2Parts.length;

    if (string1Length > string2Length) {
      return false;
    }

    for (let i = 0; i < string1Length; i += 1) {
      if (string1Parts[string1Length - i - 1] !== string2Parts[string2Length - i - 1]) {
        return false;
      }
    }

    return true;
  },
  (string1, string2) => `${string1}-${string2}`,
);

// Check wether the current input is a parent domain of any other input
const domainHasChilds = (value: string, otherValues: string[]) => {
  if (!value) return false;

  return otherValues.some((v) => v && isParentDomain(value, v));
};

const domainHasParent = (value: string, otherValues: string[]) => {
  if (!value) return false;

  // Check if any other input has the current input as a parent
  return otherValues.some((v) => v && isSubdomain(value, v));
};

export const domainIsDuplicate = (value: string, otherValues: string[]) => {
  if (!value) return false;

  // Check if any other input has the same value
  return otherValues.some((v) => value === v);
};

export interface InputValidationStatus {
  status?: 'error' | 'warning' | 'success';
  message?: string;
  showWarning?: boolean;
  warningTooltip?: string;
}

export const getInputStatus = (value?: string, otherValues?: string[]): InputValidationStatus => {
  if (!value || !otherValues) return { status: undefined, showWarning: false };

  if (domainIsDuplicate(value, otherValues)) {
    return {};
  }

  if (domainHasParent(value, otherValues)) {
    return {
      status: 'warning',
      message: t('profileInputs.azDomains.parentWarning'),
      showWarning: true,
    };
  }

  if (domainHasChilds(value, otherValues)) {
    return {
      showWarning: true,
      warningTooltip: t('profileInputs.azDomains.childWarning'),
    };
  }

  return {};
};
